import { createClient } from '@supabase/supabase-js'
import { canJoinTrip, getAvailableRoutesForTrip, getRouteDescription } from '../utils/routeAnalysis.js'
import { devWarn } from '../utils/devLog.js'
import { participantPointColumns } from './sharedTransferLocations.js'
import { mergeTripParticipantsForOwner } from '../utils/tripParticipantsMerge.js'
import { isUnauthorizedShellTrip } from '../utils/ghostTripFilter.js'
import { isWindowGlobalsEnabled } from './features'

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY

// Add validation for environment variables
if (!supabaseUrl || !supabaseAnonKey) {
  console.error('Missing Supabase environment variables. Please check your .env file.');
  console.error('REACT_APP_SUPABASE_URL:', supabaseUrl ? 'Set' : 'Missing');
  console.error('REACT_APP_SUPABASE_ANON_KEY:', supabaseAnonKey ? 'Set' : 'Missing');
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey)

// Database schema for the shared transfer system
export const TABLES = {
  TRIP_REQUESTS: 'trip_requests',
  // PII-free projection. The base table is readable only by the trip's creator
  // and admins; anything shown to other riders must come from here.
  TRIP_REQUESTS_PUBLIC: 'trip_requests_public',
  PARTICIPANTS: 'participants',
  PARTICIPANTS_PUBLIC: 'trip_participants_public',
  PROFILES_PUBLIC: 'profiles_public',
  MESSAGES: 'messages',
  PAYMENTS: 'payments'
}

// Helper functions for database operations
export const dbHelpers = {
  // Create a new trip request
  async createTripRequest(tripData, options = {}) {
    // Validate pickup and dropoff locations before attempting to insert
    if (tripData.pickup_location === tripData.dropoff_location) {
      return { 
        data: null, 
        error: { 
          message: 'Pickup and dropoff locations cannot be the same',
          details: 'Please select different locations for pickup and dropoff'
        } 
      };
    }
    
    // Get current user
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return { 
        data: null, 
        error: { 
          message: 'You must be logged in to create a trip request',
          details: 'Please log in and try again'
        } 
      };
    }
    
    // Get user profile for creator details
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', user.id)
      .single();
    
    if (profileError) {
      console.error('Error loading user profile:', profileError);
      return { 
        data: null, 
        error: { 
          message: 'Error loading user profile',
          details: 'Please try logging out and back in'
        } 
      };
    }
    
    // Add creator details to trip data
    const tripDataWithCreator = {
      ...tripData,
      creator_id: user.id,
      creator_name: profile.name,
      creator_email: user.email,
      creator_phone: options.creatorPhone?.trim() || profile.phone
    };
    
    // Insert with current schema (time_window_start and time_window_end)
    let { data, error } = await supabase
      .from(TABLES.TRIP_REQUESTS)
      .insert([tripDataWithCreator])
      .select()
    
    if (error) {
      console.error('Supabase error:', error);
      
      // Check if it's a constraint violation
      if (error.message && error.message.includes('check_different_locations')) {
        return { 
          data: null, 
          error: { 
            message: 'Pickup and dropoff locations cannot be the same',
            details: 'Please select different locations for pickup and dropoff'
          } 
        };
      }
    }
  
    return { data, error }
  },

  // Messaging: fetch DM thread between current user and counterpart(s) for a trip
  async getMessagesDM(tripId, userEmail, otherEmails = [], limit = 100) {
    try {
      const otherList = (Array.isArray(otherEmails) ? otherEmails : [otherEmails]).filter(Boolean);
      if (otherList.length === 0) {
        return { data: [], error: null };
      }

      // Safer approach: two queries, then merge in JS
      const { data: m1, error: e1 } = await supabase
        .from('messages')
        .select('*')
        .eq('trip_request_id', tripId)
        .eq('sender_email', userEmail)
        .in('recipient_email', otherList)
        .order('created_at', { ascending: true })
        .limit(limit);

      const { data: m2, error: e2 } = await supabase
        .from('messages')
        .select('*')
        .eq('trip_request_id', tripId)
        .in('sender_email', otherList)
        .eq('recipient_email', userEmail)
        .order('created_at', { ascending: true })
        .limit(limit);

      if (e1 && e2) return { data: [], error: e1 || e2 };

      const merged = [...(m1 || []), ...(m2 || [])]
        .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
        .slice(-limit);
      return { data: merged, error: null };
    } catch (error) {
      return { data: [], error };
    }
  },

  // getMessagesForTrip removed (20260802150000). It had no callers, and its
  // get_trip_messages_admin RPC was gated on admin_emails -- a table with RLS
  // disabled and authenticated INSERT, so any user could self-enrol as admin and
  // read any trip's messages. Both the function and the table are dropped;
  // is_riverside_admin() is the admin check.

  // Messaging: send a DM and persist in messages table
  async sendMessageDM({ tripId, senderId, senderName, senderEmail, recipientEmail, recipientId = null, recipientName = null, message }) {
    const { data, error } = await supabase
      .from('messages')
      .insert([{
        trip_request_id: tripId,
        sender_id: senderId,
        sender_name: senderName || senderEmail,
        sender_email: senderEmail,
        recipient_id: recipientId,
        recipient_name: recipientName || recipientEmail,
        recipient_email: recipientEmail,
        message
      }])
      .select();
    return { data, error };
  },

  // Get available trip requests (open and confirmed)
  async getOpenTripRequests() {
    // Fetch trips and sum group_size from participants for each trip
    const { data: trips, error } = await supabase
      .from(TABLES.TRIP_REQUESTS_PUBLIC)
      .select('*')
      .in('status', ['open', 'confirmed'])
      .order('created_at', { ascending: false });

    if (error) return { data: null, error };

    // For each trip, fetch participants and sum group_size
    const tripsWithCounts = await Promise.all(
      (trips || []).map(async (trip) => {
        const { data: participants } = await supabase
          .from(TABLES.PARTICIPANTS_PUBLIC)
          .select('*')
          .eq('trip_request_id', trip.id);
        
        // Count only authorized/paid seats to avoid inflating capacity with pending joins
        const seatsFilled = (participants || [])
          .filter(p => p.payment_status === 'authorized' || p.payment_status === 'paid')
          .reduce((sum, p) => sum + (p.group_size || 1), 0);

        return { 
          ...trip, 
          seats_filled: seatsFilled,
          participants: participants || []
        };
      })
    );
    return { data: tripsWithCounts, error: null };
  },

  // Get user's trips (both created and joined)
  async getUserTrips() {
    // Get current user
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return { data: null, error: { message: 'User not authenticated' } };
    }

    // Get trips created by user
    const { data: createdTrips, error: createdError } = await supabase
      .from(TABLES.TRIP_REQUESTS)
      .select('*')
      .eq('creator_id', user.id)
      .order('created_at', { ascending: false });

    if (createdError) return { data: null, error: createdError };

    // Get trips joined by user
    const { data: joinedParticipants, error: joinedError } = await supabase
      .from(TABLES.PARTICIPANTS)
      .select('trip_request_id')
      .eq('user_id', user.id);

    if (joinedError) return { data: null, error: joinedError };

    const joinedTripIds = (joinedParticipants || []).map(p => p.trip_request_id).filter(id => id != null);
    
    // Only query for joined trips if there are valid IDs
    let joinedTrips = [];
    if (joinedTripIds.length > 0) {
      // Joined (not created) trips: the creator's contact details are not ours to see.
      const { data: joinedTripsData, error: joinedTripsError } = await supabase
        .from(TABLES.TRIP_REQUESTS_PUBLIC)
        .select('*')
        .in('id', joinedTripIds)
        .order('created_at', { ascending: false });
      
      if (joinedTripsError) return { data: null, error: joinedTripsError };
      joinedTrips = joinedTripsData || [];
    }

    // Deduplicate by trip ID. Joined trips use trip_requests_public (no creator PII);
    // created trips use the base table. When the user both created and joined, keep the
    // full row so My Trips still shows their own contact details.
    const uniqueTripsMap = {};
    (joinedTrips || []).forEach((trip) => {
      if (trip?.id) uniqueTripsMap[trip.id] = trip;
    });
    (createdTrips || []).forEach((trip) => {
      if (trip?.id) uniqueTripsMap[trip.id] = trip;
    });
    const uniqueTrips = Object.values(uniqueTripsMap);

    const tripsWithParticipants = await Promise.all(
      uniqueTrips.map(async (trip) => {
        const { data: publicRows, error: publicRowsError } = await supabase
          .from(TABLES.PARTICIPANTS_PUBLIC)
          .select('*')
          .eq('trip_request_id', trip.id);
        const { data: ownRow, error: ownRowError } = await supabase
          .from(TABLES.PARTICIPANTS)
          .select('*')
          .eq('trip_request_id', trip.id)
          .eq('user_id', user.id)
          .maybeSingle();

        const participantReadFailed = Boolean(publicRowsError || ownRowError);
        const participants = mergeTripParticipantsForOwner(publicRows, ownRow);

        const seatsFilled = participants
          .filter((p) => p.payment_status === 'authorized' || p.payment_status === 'paid')
          .reduce((sum, p) => sum + (p.group_size || 1), 0);
        
        return {
          ...trip,
          seats_filled: seatsFilled,
          participants: participants || [],
          participantReadFailed,
        };
      })
    );

    const visibleTrips = tripsWithParticipants
      .filter(
        (trip) =>
          trip.participantReadFailed ||
          !isUnauthorizedShellTrip(trip, trip.participants),
      )
      .map(({ participantReadFailed: _participantReadFailed, ...trip }) => trip);

    return { data: visibleTrips, error: null };
  },

  // Join a trip request (with segment information)
  async joinTripRequest(tripId, userData, groupSize, segment = null) {
    // Get current user
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return { data: null, error: { message: 'User not authenticated' } };
    }

    // Get user profile for participant details
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', user.id)
      .single();
    
    if (profileError) {
      console.error('Error loading user profile for participant:', profileError);
      return { 
        data: null, 
        error: { 
          message: 'Error loading user profile',
          details: 'Please try logging out and back in'
        } 
      };
    }

    // Calculate the cost for this user
    let paymentAmount = 0;
    try {
      const { data: trip } = await this.getTripById(tripId);
      if (trip && segment) {
        const { calculateUserCost } = await import('../utils/pricing.js');
        paymentAmount = await calculateUserCost(trip, segment, groupSize || 1, this);
      }
    } catch (error) {
      console.error('Error calculating payment amount:', error, { tripId, segment, groupSize });
      // Continue with default amount - don't let pricing errors break the join process
      paymentAmount = 0;
    }

    const pointFields = participantPointColumns(
      userData.pickupPoint,
      userData.dropoffPoint,
    );

    const { data, error } = await supabase
      .from(TABLES.PARTICIPANTS)
      .insert([{
        trip_request_id: tripId,
        user_id: user.id,
        user_name: profile.name,
        user_email: user.email,
        user_phone: userData.contact_phone?.trim() || profile.phone,
        group_size: groupSize || 1,
        segment: segment || 'NairobiToMoshi',
        pickup_location: userData.pickup_location,
        dropoff_location: userData.dropoff_location,
        order_notes: userData.order_notes?.trim() || null,
        ...pointFields,
        payment_amount: paymentAmount,
        payment_status: 'pending'
      }])
      .select();

    if (error) {
      const { extractJoinRejectionReason, joinRejectionMessage } = await import('./sharedTransferRules.js');
      const reason = extractJoinRejectionReason(error);
      if (reason) {
        return {
          data: null,
          error: {
            message: joinRejectionMessage(reason),
            reason,
          },
        };
      }
    }

    // Recalculate costs for all participants after someone joins
    if (!error && tripId) {
      try {
        const { recalculateAllParticipantCosts } = await import('../utils/pricing.js');
        await recalculateAllParticipantCosts(tripId, this);

        // Emit trip joined event with user details for updates
        const tripEventEmitter = (await import('../utils/tripEvents')).default;
        const { TRIP_EVENTS } = await import('../utils/tripEvents');

        // Emit with correct data structure for trip updates
        const eventData = { 
          trip_id: tripId,
          user_email: user.email,
          user_name: profile.name,
          group_size: groupSize || 1,
          segment: segment
        };
        tripEventEmitter.emit(TRIP_EVENTS.TRIP_JOINED, eventData);

        // Also emit generic update event
        tripEventEmitter.emit(TRIP_EVENTS.TRIP_UPDATED, { tripId });
      } catch (recalcError) {
        console.error('Error recalculating costs:', recalcError, { tripId });
        // Don't let recalculation errors break the join process
      }
    }

    return { data, error };
  },

  // Add creator as participant (with initial rate)
  async addCreatorAsParticipant(tripId, userData, groupSize, segment) {
    // Calculate the creator cost (initial rate)
    let paymentAmount = 0;
    try {
      const { data: trip } = await this.getTripById(tripId);
      if (trip && segment) {
        const { calculateCreatorCost } = await import('../utils/pricing.js');
        paymentAmount = await calculateCreatorCost(trip, segment, groupSize || 1, this);
      }
    } catch (error) {
      console.error('Error calculating creator payment amount:', error);
      // Continue with default amount
    }

    // Get current user
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return { data: null, error: { message: 'User not authenticated' } };
    }

    // Get user profile for participant details
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', user.id)
      .single();
    
    if (profileError) {
      console.error('Error loading user profile for participant:', profileError);
      return { 
        data: null, 
        error: { 
          message: 'Error loading user profile',
          details: 'Please try logging out and back in'
        } 
      };
    }

    const pointFields = participantPointColumns(
      userData.pickupPoint,
      userData.dropoffPoint,
    );

    const participantData = {
      trip_request_id: tripId,
      user_id: user.id,
      user_name: profile.name,
      user_email: user.email,
      user_phone: userData.contact_phone?.trim() || profile.phone,
      group_size: groupSize || 1,
      segment: segment,
      pickup_location: userData.pickup_location,
      dropoff_location: userData.dropoff_location,
      order_notes: userData.order_notes?.trim() || null,
      ...pointFields,
      payment_status: 'pending',
      payment_amount: paymentAmount
    };

    const { data, error } = await supabase
      .from(TABLES.PARTICIPANTS)
      .insert([participantData])
      .select();

    // seats_filled is owned by update_trip_seats_and_status (20260726120000), which
    // recomputes it as SUM(group_size) over authorized/paid rows. The client-side
    // increment that used to live here double-counted the row we just inserted --
    // it is 'pending', so the trigger deliberately excludes it -- and is now
    // rejected outright by the trip_requests column allowlist (20260802120000).

    return { data, error };
  },

  async updateParticipantPoints(participantId, { pickupPoint, dropoffPoint, orderNotes } = {}) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return { data: null, error: { message: 'User not authenticated' } };
    }

    const updatePayload = {
      ...participantPointColumns(pickupPoint, dropoffPoint),
      updated_at: new Date().toISOString(),
    };
    if (orderNotes !== undefined) {
      updatePayload.order_notes = orderNotes?.trim() || null;
    }

    const { data, error } = await supabase
      .from(TABLES.PARTICIPANTS)
      .update(updatePayload)
      .eq('id', participantId)
      .eq('user_id', user.id)
      .select();

    return { data, error };
  },

  // Calculate cost for trip creator (first group)
  async calculateCreatorCost(tripData, creatorSegment, creatorGroupSize) {
    const { calculateCreatorCost } = await import('../utils/pricing');
    return calculateCreatorCost(tripData, creatorSegment, creatorGroupSize, this);
  },

  // Browser-driven auto-charging removed (20260802150000).
  //
  // chargeConfirmedTripParticipants + processTripsNeedingCharge were the only
  // callers of get_trips_needing_charge / mark_charging_processed /
  // mark_charging_skipped, and were reached only from AutoChargingMonitor, which
  // was never mounted. Charging runs server-side: pg_cron -> auto-charger Edge
  // Function (20260721120000). Those RPCs are now service_role only.

  // Get participants for a trip (public-safe columns for browsing and pricing)
  async getTripParticipants(tripId) {
    if (!tripId) {
      console.error('No trip ID provided to getTripParticipants');
      return { data: null, error: 'No trip ID provided' };
    }
    
    const { data, error } = await supabase
      .from(TABLES.PARTICIPANTS_PUBLIC)
      .select('*')
      .eq('trip_request_id', tripId);
    
    return { data, error };
  },

  async getMyParticipantForTrip(tripId) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return { data: null, error: { message: 'User not authenticated' } };
    }

    const { data, error } = await supabase
      .from(TABLES.PARTICIPANTS)
      .select('*')
      .eq('trip_request_id', tripId)
      .eq('user_id', user.id)
      .maybeSingle();

    return { data, error };
  },

  // Get participants by segment
  async getParticipantsBySegment(tripId, segment) {
    const { data, error } = await supabase
      .from(TABLES.PARTICIPANTS_PUBLIC)
      .select('*')
      .eq('trip_request_id', tripId)
      .eq('segment', segment);
    
    return { data, error };
  },

  // Get trip by ID.
  // RLS on trip_requests returns a row only to the trip's creator (and admins), so
  // the base table doubles as the "am I the owner" check: a hit means we may show
  // the full record, a miss means we fall back to the PII-free view.
  async getTripById(tripId) {
    const { data: ownTrip } = await supabase
      .from(TABLES.TRIP_REQUESTS)
      .select('*')
      .eq('id', tripId)
      .maybeSingle();

    if (ownTrip) return { data: ownTrip, error: null };

    const { data, error } = await supabase
      .from(TABLES.TRIP_REQUESTS_PUBLIC)
      .select('*')
      .eq('id', tripId)
      .single();

    return { data, error };
  },

  // Get trip by ID with participants
  async getTripWithParticipants(tripId) {
    const { data: trip, error: tripError } = await dbHelpers.getTripById(tripId);

    if (tripError) return { data: null, error: tripError };

    const { data: participants, error: participantsError } = await supabase
      .from(TABLES.PARTICIPANTS_PUBLIC)
      .select('*')
      .eq('trip_request_id', tripId);
    
    if (participantsError) return { data: null, error: participantsError };
    
    return { 
      data: { 
        ...trip, 
        participants: participants || [],
        seats_filled: (participants || []).reduce((sum, p) => sum + (p.group_size || 1), 0)
      }, 
      error: null 
    };
  },

  // Update trip status
  async updateTripStatus(tripId, status) {
    const { data, error } = await supabase
      .from(TABLES.TRIP_REQUESTS)
      .update({ status })
      .eq('id', tripId)
      .select();
    
    return { data, error };
  },

  // Deprecated: payment_status/payment_amount are set server-side via stripe-payment-handler.
  async updateParticipantPaymentStatus(tripId, userEmail, paymentStatus, amount = null) {
    devWarn(
      'updateParticipantPaymentStatus is deprecated. Use stripe-payment-handler confirm_participant_payment instead.',
      { tripId, paymentStatus },
    );
    return {
      data: null,
      error: { message: 'updateParticipantPaymentStatus is deprecated; use server confirm flow' },
    };
  },

  // Create payment record
  async createPayment(tripId, participantId, amount, paymentMethod = 'stripe') {
    const { data, error } = await supabase
      .from(TABLES.PAYMENTS)
      .insert([{
        trip_request_id: tripId,
        participant_id: participantId,
        amount: amount,
        payment_method: paymentMethod,
        status: 'pending'
      }])
      .select();
    
    return { data, error };
  },

  // Update payment status
  async updatePaymentStatus(paymentId, status, stripePaymentIntentId = null) {
    const { data, error } = await supabase
      .from(TABLES.PAYMENTS)
      .update({ 
        status,
        stripe_payment_intent_id: stripePaymentIntentId,
        updated_at: new Date().toISOString()
      })
      .eq('id', paymentId)
      .select();
    
    return { data, error };
  },

  // Route validation functions
  async validateRouteForJoining(tripId, joinerRoute) {
    try {
      // Get trip details
      const { data: trip, error: tripError } = await this.getTripById(tripId);
      if (tripError || !trip) {
        console.error('Trip not found:', tripError);
        return { 
          canJoin: false, 
          error: 'Trip not found' 
        };
      }

      // Get existing participants
      const { data: participants, error: participantsError } = await this.getTripParticipants(tripId);
      if (participantsError) {
        console.error('Error loading participants:', participantsError);
        return { 
          canJoin: false, 
          error: 'Error loading trip participants' 
        };
      }

      // Add isCreator flag to participants
      const participantsWithCreatorFlag = (participants || []).map(p => ({
        ...p,
        isCreator: p.user_id === trip.creator_id
      }));

      // Check if joiner can join based on route sharing
      const canJoin = canJoinTrip(trip, participantsWithCreatorFlag, joinerRoute);

      const originOk = !joinerRoute.pickup
        || joinerRoute.pickup === trip.pickup_location;

      return { 
        canJoin: canJoin && originOk, 
        error: originOk ? null : 'Joiners must board at the trip origin.',
        trip,
        participants: participantsWithCreatorFlag
      };
    } catch (error) {
      console.error('Error validating route for joining:', error);
      return { 
        canJoin: false, 
        error: 'Error validating route' 
      };
    }
  },

  async getAvailableRoutesForTrip(tripId) {
    try {
      // Get trip details
      const { data: trip, error: tripError } = await this.getTripById(tripId);
      if (tripError || !trip) {
        return { 
          data: [], 
          error: 'Trip not found' 
        };
      }

      // Get existing participants
      const { data: participants, error: participantsError } = await this.getTripParticipants(tripId);
      if (participantsError) {
        return { 
          data: [], 
          error: 'Error loading trip participants' 
        };
      }

      // Add isCreator flag to participants
      const participantsWithCreatorFlag = (participants || []).map(p => ({
        ...p,
        isCreator: p.user_id === trip.creator_id
      }));

      // Get available routes
      const availableRoutes = getAvailableRoutesForTrip(trip, participantsWithCreatorFlag);

      return { 
        data: availableRoutes, 
        error: null 
      };
    } catch (error) {
      console.error('Error getting available routes for trip:', error);
      return { 
        data: [], 
        error: 'Error getting available routes' 
      };
    }
  },

  async getRouteDescription(pickup, dropoff, direction) {
    return getRouteDescription(pickup, dropoff, direction);
  }
}

// DevTools debug scripts (test_trip_status_fix.js, debug_segments.js, …)
// expect these on window. Production stays dark unless
// REACT_APP_ENABLE_WINDOW_GLOBALS is explicitly true at build time.
if (typeof window !== 'undefined' && isWindowGlobalsEnabled()) {
  window.supabase = supabase;
  window.dbHelpers = dbHelpers;
}
